import { AxiosError, AxiosResponse } from 'axios'
import { camelize, snakeize } from 'casing'
import { AnyAction } from 'redux'
import {
  fetchResellerAgreementData,
  getSingleResaleAuthorization,
  patchResellerData,
  deleteSingleResaleAuthorization,
} from '../../../flyout/api/markeplace'
import {
  getMicrosoftResaleAuthorization,
  patchMicrosoftResaleAuthorization,
  reminderMicrosoftResaleAuthorization,
  getAzureMpposData,
  patchResetMicrosoftResaleAuthorization,
  patchAzurePrivateOffersData,
} from '../../../flyout/api/azure'
import type { RootState, AppDispatch } from '../../../store'
import { getErrorMessages } from '../../../common/utils/error'
import {
  PatchResaleAuthorizationGeneralMessage,
  RequestFailureMessage,
  ResellerPatchSuccessMessage,
  sendReminderToBuyerSuccess,
  DeleteResaleAuthorizationSuccessMessage,
  DeleteResaleAuthorizationFailureMessage,
} from '../../../common/utils/messagesContants'
import { updateAppAlert } from '../../../common/modules/appAlert/actions'
import { LoadingTypes, startLoading, stopLoading } from '@labrav/common'
import {
  ResaleAuthorizationsResellerAgreements,
  ResaleAuthorizationsState,
  ResellerAgreementStatus,
  Stakeholders,
} from './reducer'
import { errorLogger } from '../../../common/utils/errorLogger'
import { get, isEmpty } from 'lodash'
import { CloudMarketplace } from '@labrav/react-components/lib/@types/types/forms'
import { getAzureMappedPlans } from '../../../flyout/modules/resaleAuthorization/actions'
import { Product } from '../../../flyout/modules/productsListing/reducer'
import { UserState } from '../../../oppsync/modules/userList/reducer'
import { getAllPartnerUserEmails } from '../../../common/api/index'
import { ResaleAuthorizationsActionTypes } from './actionTypes'

export { ResaleAuthorizationsActionTypes } from './actionTypes'

// `selectedInput` is the DB column/field name to filter on, coming from
// searchDropDownOptionMapping.resaleAuthorizationOptions ('agreement_name',
// 'reseller_name' or 'aws_agreement_id'). Maps it to the explicit query param
// fetchResellerAgreementData expects.
const getResellerAgreementSearchParams = (
  input?: string,
  selectedInput?: string
) => {
  if (isEmpty(input)) {
    return {}
  }
  switch (selectedInput) {
    case 'agreement_name':
      return { agreementName: input }
    case 'reseller_name':
      return { resellerName: input }
    case 'aws_agreement_id':
      return { awsAgreementId: input }
    default:
      return {}
  }
}

export const processResellerAgreementData = async (
  partnerId: string,
  pageSize: number,
  pageNumber: number,
  productId?: string,
  crmOpportunityId?: string,
  input?: string,
  selectedInput?: string,
  sectionName?: string
) => {
  // Resale authorizations search now queries the marketplace service directly
  // (DB-backed) instead of going through the OpenSearch based search service.
  const response = await fetchResellerAgreementData({
    partnerId,
    pageSize,
    pageNumber,
    sortBy: 'created_at',
    sortOrder: 'desc',
    productId,
    crmOpportunityId,
    status: '',
    origin: 'CREATED',
    ...getResellerAgreementSearchParams(input, selectedInput),
  })
  return {
    ...response,
    data: {
      ...response?.data,
      reseller_agreements: response?.data?.aws_reseller_agreements,
    },
  }
}

const processResellerAgreementDataByCloud = async (
  partnerId: string,
  pageSize: number,
  pageNumber: number,
  cloud: CloudMarketplace,
  productId?: string,
  crmOpportunityId?: string,
  input?: string,
  selectedInput?: string,
  sectionName?: string,
  expiryDays?: string,
  offerStatus?: string[]
) => {
  switch (cloud) {
    case 'AWS':
      return await processResellerAgreementData(
        partnerId,
        pageSize,
        pageNumber,
        productId,
        crmOpportunityId,
        input,
        selectedInput,
        sectionName
      )
    case 'AZURE':
      const response = await getAzureMpposData({
        partnerId,
        pageSize,
        pageNumber,
        productId,
        crmOpportunityId,
        expiryDays,
        offerStatus,
      })
      return {
        ...response,
        data: {
          ...response?.data,
          reseller_agreements: response?.data?.mppos,
          count: response?.data?.total_count,
        },
      }
    case 'GCP':
      return { data: [] }
    default:
      return { data: [] }
  }
}
export const getResellerAgreementData =
  ({
    partnerId,
    pageSize = 10,
    pageNumber = 1,
    cloud,
    productId,
    crmOpportunityId,
    input,
    selectedInput,
    sectionName,
    expiryDays,
    offerStatus,
  }: {
    partnerId: string
    pageSize?: number
    pageNumber?: number
    cloud: CloudMarketplace
    productId?: string
    crmOpportunityId?: string
    input?: string
    selectedInput?: string
    sectionName?: string
    expiryDays?: string
    offerStatus?: string[]
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const { data } = await processResellerAgreementDataByCloud(
        partnerId,
        pageSize,
        pageNumber,
        cloud,
        productId,
        crmOpportunityId,
        input,
        selectedInput,
        sectionName,
        expiryDays,
        offerStatus
      )
      await dispatch(
        setResaleAuthorizations(camelize(data), pageSize, pageNumber)
      )
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }

export const setResaleAuthorizations = (
  agreements: ResaleAuthorizationsState,
  pageSize: number,
  pageNumber: number
) => ({
  type: ResaleAuthorizationsActionTypes.RESALE_SET_RESELLER_DATA,
  payload: { agreements, pageSize, pageNumber },
})

export const setResaleAuthorizationsPageNumber = (pageNumber: number) =>
  ({
    type: ResaleAuthorizationsActionTypes.RESALE_SET_PAGE_NUMBER,
    payload: pageNumber,
  }) as unknown as AnyAction

export const updateResaleAuthorizationsPageSize =
  ({
    partnerId,
    pageSize,
    cloud,
    productId,
  }: {
    partnerId: string
    pageSize: number
    cloud: CloudMarketplace
    productId?: string
    crmOpportunityId?: string
  }) =>
  async (dispatch: AppDispatch) => {
    await dispatch(setResaleAuthorizationsPageSize(pageSize))
    await dispatch(
      getResellerAgreementData({
        partnerId,
        pageSize,
        pageNumber: 1,
        productId,
        cloud,
      }) as unknown as AnyAction
    )
  }

export const setResaleAuthorizationsPageSize = (pageSize: number) => ({
  type: ResaleAuthorizationsActionTypes.RESALE_SET_LIMIT,
  payload: pageSize,
})

export type UpdateResellerDataToBeSent = {
  status: ResellerAgreementStatus
  awsAgreementId: string
}
export const updateResellerData =
  (partnerId: string, agreementId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(startLoading(LoadingTypes.GENERAL))
    try {
      const agreement =
        getState().cppoReseller.resellerAgreements.find(
          agreement => agreement.id === agreementId
        ) || ({} as ResaleAuthorizationsResellerAgreements)

      const dataToBeSent = {
        status: agreement.status || ResellerAgreementStatus.IN_PROGRESS,
        awsAgreementId: agreement.awsAgreementId,
      }
      await patchResellerData(partnerId, agreementId, snakeize(dataToBeSent))
      dispatch(
        updateAppAlert({
          message: ResellerPatchSuccessMessage,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error) {
      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(
            error as AxiosResponse<ErrorResponse>
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )

      const globalState = getState()
      errorLogger({ globalState })(error as Error)
    } finally {
      dispatch(stopLoading(LoadingTypes.GENERAL))
    }
  }
export const updateAgreementId = (
  agreementId: string,
  awsAgreementId: string
) => ({
  type: ResaleAuthorizationsActionTypes.UPDATE_AGREEMENT_ID,
  payload: { agreementId, awsAgreementId },
})
export const updateAgreementStatus = (
  agreementId: string,
  resellerAgreementStatus: ResellerAgreementStatus
) => ({
  type: ResaleAuthorizationsActionTypes.UPDATE_AGREEMENT_STATUS,
  payload: { agreementId, resellerAgreementStatus },
})

export const clearResaleAuthorizations = () => ({
  type: ResaleAuthorizationsActionTypes.RESALE_CLEAR_RESELLER_DATA,
})

const getSingleResaleAuthorizationByCloud = async (
  partnerId: string,
  resaleAuthorizationId: string,
  cloud: CloudMarketplace,
  crmOpportunityId?: string
) => {
  switch (cloud) {
    case 'AWS':
      return await getSingleResaleAuthorization(
        partnerId,
        resaleAuthorizationId,
        crmOpportunityId
      )
    case 'AZURE':
      return await getMicrosoftResaleAuthorization({
        partnerId,
        resaleAuthorizationId,
        crmOpportunityId,
      })
    case 'GCP':
      return
    default:
      return
  }
}

export const getCurrentResaleAuthorization =
  ({
    cloud,
    partnerId,
    resaleAuthorizationId,
    crmOpportunityId,
    productId,
  }: {
    partnerId: string
    resaleAuthorizationId: string
    cloud: CloudMarketplace
    crmOpportunityId?: string
    productId?: string
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.GET_SINGLE_RESALE_AUTHORIZATION))
    await dispatch(clearResaleAuthorizations())
    try {
      const response = await getSingleResaleAuthorizationByCloud(
        partnerId,
        resaleAuthorizationId,
        cloud,
        crmOpportunityId
      )

      if (response?.data) {
        const product =
          getState().productsListing[cloud].products.rows.find(
            product => product.productId === productId
          ) || ({} as Product)
        const userList = getState().userList?.user?.userList?.users
        const usersEmails = await getAllPartnerUserEmails(partnerId)
        dispatch(
          setCurrentResaleAuthorization(
            transformSingleResaleAuthorization({
              data: response?.data,
              cloud,
              product,
              userList,
              usersEmails,
            })
          )
        )
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.GET_SINGLE_RESALE_AUTHORIZATION))
    }
  }

export const setCurrentResaleAuthorization = (data: Record<string, unknown>) =>
  ({
    type: ResaleAuthorizationsActionTypes.SET_CURRENT_RESALE_AUTHORIZATION,
    payload: data,
  }) as unknown as AnyAction

export const clearCurrentResaleAuthorization = () =>
  ({
    type: ResaleAuthorizationsActionTypes.CLEAR_CURRENT_RESALE_AUTHORIZATION,
  }) as unknown as AnyAction

export const updateResaleAuthorizationForUser =
  (
    resaleAuthorizationId: string,
    data: Record<string, unknown>,
    crmOpportunityId?: string,
    cloud: CloudMarketplace = 'AWS',
    saveAsDraft?: boolean,
    handleSuccess?: () => void,
    successMessage?: string,
    editOffer?: boolean,
    partnerId?: string
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.UPDATE_RESALE_AUTHORIZATION))
    try {
      const partnerIdToUse =
        partnerId || getState().AccountData.user.partnerData?.partnerId || ''
      await patchAPIresaleAuthorization({
        partnerId: partnerIdToUse,
        resaleAuthorizationId,
        data: snakeize({ crmOpportunityId, ...data }),
        saveAsDraft: cloud === 'AWS' ? false : saveAsDraft,
        cloud,
        editOffer,
      })
      dispatch(
        updateAppAlert({
          message: successMessage || PatchResaleAuthorizationGeneralMessage,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      if (handleSuccess) {
        await handleSuccess()
      }
      await dispatch(
        getCurrentResaleAuthorization({
          cloud,
          partnerId: partnerIdToUse,
          resaleAuthorizationId,
          crmOpportunityId,
        }) as unknown as AnyAction
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.UPDATE_RESALE_AUTHORIZATION))
    }
  }

export const patchAPIresaleAuthorization = async ({
  data,
  cloud,
  saveAsDraft,
  partnerId,
  resaleAuthorizationId,
  crmOpportunityId,
  editOffer,
}: {
  data: Record<string, unknown>
  cloud?: CloudMarketplace
  partnerId: string
  saveAsDraft?: boolean
  resaleAuthorizationId: string
  crmOpportunityId?: string
  editOffer?: boolean
}) => {
  switch (cloud) {
    case 'AWS': {
      return await patchResellerData(
        partnerId,
        resaleAuthorizationId,
        snakeize({ crmOpportunityId, ...data }),
        saveAsDraft
      )
    }
    case 'AZURE': {
      return await patchMicrosoftResaleAuthorization({
        data: snakeize({ crmOpportunityId, ...data }),
        saveAsDraft,
        partnerId,
        resaleAuthorizationId,
        editOffer,
      })
    }
    case 'GCP': {
      return
    }
    case 'REDHAT': {
      return
    }
  }
}

export const transformSingleResaleAuthorization = ({
  data,
  cloud,
  product,
  userList,
  usersEmails,
}: {
  data: Record<string, unknown>
  cloud: CloudMarketplace
  product: Product
  userList: UserState[]
  usersEmails?: string[]
}) => {
  switch (cloud) {
    case 'AWS': {
      return camelize(data)
    }
    case 'AZURE': {
      const { plans, ...remainingResaleAuthorizationDetail } = data

      const {
        resellerDetails,
        offerRecipients,
        links,
        cloud,
        offerStakeholders,
        eulaFiles,
        emailCustomMessage,
        metaData,
        ...remainingResaleAuthorization
      } = camelize(remainingResaleAuthorizationDetail)

      const azurePricingType = get(plans, '0.discount_type', 'absolute')

      const users = usersEmails?.length
        ? usersEmails
        : userList.map(({ email }) => email)
      const existingStakeHolder =
        (offerStakeholders || [])?.filter((item: Stakeholders) =>
          users.includes(item.email)
        ) || []
      const stackHolderCustom =
        (offerStakeholders || [])?.filter(
          (item: Stakeholders) => !users.includes(item.email)
        ) || []

      return {
        ...camelize({
          ...remainingResaleAuthorization,
          eulaFiles: eulaFiles || [],
          emailCustomMessage: emailCustomMessage || '',
          resellerDetails: resellerDetails || [],
          offerRecipients: offerRecipients?.length
            ? offerRecipients?.map((item: any) => ({
                ...item,
              }))
            : [],
          metaData: (metaData || [])?.length ? metaData : [],
          azurePricingType,
          offerStakeholders: [...existingStakeHolder],
          customOfferStakeholders: [...stackHolderCustom],
        }),
        plans: ((plans as Record<string, any>[]) || [])?.length
          ? getAzureMappedPlans({
              plans: plans as Record<string, any>[],
              product,
            })
          : [],
      }
    }
    case 'GCP': {
      return camelize(data)
    }
    case 'REDHAT': {
      return camelize(data)
    }
  }
  return camelize(data)
}

export const reminderResaleAuthorization =
  ({
    resaleAuthorizationId,
    data,
    handleSuccess,
    partnerId,
  }: {
    resaleAuthorizationId: string
    data: Record<string, unknown>
    handleSuccess?: () => void
    partnerId?: string
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(
      startLoading(LoadingTypes.UPDATE_FLYOUT_RESELLER_AUTHORIZATION)
    )
    try {
      const partnerIdToUse =
        partnerId || getState().AccountData.user.partnerData?.partnerId || ''
      await reminderMicrosoftResaleAuthorization({
        partnerId: partnerIdToUse,
        resaleAuthorizationId,
        data: snakeize(data),
      })
      dispatch(
        updateAppAlert({
          message: sendReminderToBuyerSuccess,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      if (handleSuccess) {
        await handleSuccess()
      }
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(
        stopLoading(LoadingTypes.UPDATE_FLYOUT_RESELLER_AUTHORIZATION)
      )
    }
  }

export const editFailedResaleAuthorizationMPPO =
  ({
    resaleAuthorizationId,
    handleSuccess,
    partnerId,
  }: {
    resaleAuthorizationId: string
    handleSuccess?: () => void
    partnerId?: string
  }) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    await dispatch(startLoading(LoadingTypes.UPDATE_RESALE_AUTHORIZATION))
    try {
      const partnerIdToUse =
        partnerId || getState().AccountData.user.partnerData?.partnerId || ''
      await patchResetMicrosoftResaleAuthorization({
        partnerId: partnerIdToUse,
        resaleAuthorizationId,
      })
      dispatch(
        updateAppAlert({
          message: PatchResaleAuthorizationGeneralMessage,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
      if (handleSuccess) {
        await handleSuccess()
      }
      await dispatch(
        getCurrentResaleAuthorization({
          cloud: 'AZURE',
          partnerId: partnerIdToUse,
          resaleAuthorizationId,
          crmOpportunityId: '',
        }) as unknown as AnyAction
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([RequestFailureMessage])(errorResponse),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      await dispatch(stopLoading(LoadingTypes.UPDATE_RESALE_AUTHORIZATION))
    }
  }

export const deleteCurrentResaleAuthorization =
  (
    agreementId: string,
    callBack?: () => void,
    partnerId?: string,
    cloudMarketplace?: CloudMarketplace
  ) =>
  async (dispatch: AppDispatch, getState: () => RootState) => {
    const cloud =
      cloudMarketplace || getState().productListingFilters.cloudMarketplace

    const { pageNumber, pageSize } = getState().cppoReseller
    await dispatch(startLoading(LoadingTypes.DELETE_RESALE_AUTHORIZATION))
    try {
      const partnerIdToUse =
        partnerId || getState().AccountData.user.partnerData?.partnerId || ''
      await deleteOfferBasedOnCloud(cloud, partnerIdToUse, agreementId)
      await dispatch(clearResaleAuthorizations())
      await dispatch(
        getResellerAgreementData({
          partnerId: partnerIdToUse,
          cloud,
          pageSize,
          pageNumber,
        }) as unknown as AnyAction
      )

      dispatch(
        updateAppAlert({
          message: DeleteResaleAuthorizationSuccessMessage,
          messageType: 'SUCCESS',
          autoClose: true,
        })
      )
    } catch (error: any) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      dispatch(
        updateAppAlert({
          message: getErrorMessages([DeleteResaleAuthorizationFailureMessage])(
            errorResponse
          ),
          messageType: 'ERROR',
          autoClose: true,
        })
      )
      const globalState = getState()
      errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      if (callBack) {
        await callBack()
      }
      await dispatch(stopLoading(LoadingTypes.DELETE_RESALE_AUTHORIZATION))
    }
  }

const deleteOfferBasedOnCloud = async (
  cloud: CloudMarketplace,
  partnerId: string,
  resaleAuthorizationId: string
) => {
  switch (cloud) {
    case 'AWS': {
      return await deleteSingleResaleAuthorization(
        partnerId,
        resaleAuthorizationId
      )
    }
    case 'AZURE': {
      // Azure uses PATCH to delete/withdraw private offers by updating state
      return await patchAzurePrivateOffersData(
        partnerId,
        resaleAuthorizationId,
        snakeize({ state: 'DELETE' })
      )
    }
    case 'GCP': {
      // Yet to be implemented
      return
    }
  }
}
